import { toast } from 'sonner';

import { extractApiErrorMessage } from '@/lib/api-client';
import type {
  useReviewInvoiceMutation,
  ReviewInvoiceInput,
} from '../api/use-review-invoice-mutation';
import type { usePreviewInvoicePdf } from '../api/use-preview-invoice-pdf';

export type ReviewMutation = ReturnType<typeof useReviewInvoiceMutation>;
export type PreviewMutation = ReturnType<typeof usePreviewInvoicePdf>;

// Save the pending review (when there are changes) then render a Draft preview,
// so what the operator sees is what was saved. Persisting first keeps a single
// render path — the preview only ever sees the allowlist-filtered persisted
// overrides, never an ephemeral client override channel. Returns the rendered
// bytes for the inline pane, or null when the save or render failed.
export async function runPreview(
  args: Readonly<{
    body: ReviewInvoiceInput | null;
    save: ReviewMutation;
    preview: PreviewMutation;
    includeChainHistory: boolean;
    onSaved: () => void;
  }>,
): Promise<Blob | null> {
  try {
    if (args.body !== null) {
      await args.save.mutateAsync(args.body);
      args.onSaved();
    }
    return await args.preview.mutateAsync({ includeChainHistory: args.includeChainHistory });
  } catch (err) {
    toast.error(
      err instanceof Error
        ? extractApiErrorMessage(err, 'Could not render the preview.')
        : 'Could not render the preview.',
    );
    return null;
  }
}

export function submitReview(
  mutation: ReviewMutation,
  body: ReviewInvoiceInput | null,
  callbacks: Readonly<{ onClose: () => void; onSaved: () => void }>,
): void {
  if (body === null) {
    callbacks.onClose();
    return;
  }
  mutation.mutate(body, {
    onSuccess: () => {
      toast.success('Review saved.');
      callbacks.onSaved();
    },
    onError: (err) => {
      toast.error(extractApiErrorMessage(err, 'Could not save the review.'));
    },
  });
}
